import { useToast } from "@/shared/hooks/use-toast";
import { useEffect, useState } from "react";
import {
  ServiceContainer,
  UpdateTargetRevenueRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import { useQpmCalcServiceGroupTargetRevenuePostMutation } from "@/services/backend/qpm/calculations/api/calculation-api";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Check, Edit2, Loader2, X } from "lucide-react";
import { Label } from "@/shared/components/ui/label";
import t from "@/lang/lang";
import {
  FloatNumberInput,
  Value,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { CalculationServiceType } from "@/services/backend/qpm/calculations/types/calculation-enum";

export type EditRevenueComponentProps = {
  calculationId: string;
  serviceGroup: ServiceContainer;
  serviceTypeKey: CalculationServiceType;
};

export function EditRevenueComponent({
  calculationId,
  serviceGroup,
  serviceTypeKey,
}: EditRevenueComponentProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState<boolean>(false);
  const [request, setRequest] = useState<UpdateTargetRevenueRequest>({
    calculationId,
    serviceTypeKey,
    targetRevenue: serviceGroup.targetRevenue,
  });

  const [updateTargetRevenue, { isLoading, error, reset }] =
    useQpmCalcServiceGroupTargetRevenuePostMutation();

  const onSubmit = () => {
    updateTargetRevenue(request);
  };

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <Button
          className="h-fit w-fit p-1"
          onClick={() => setOpen(!open)}
          size="icon"
          variant="outline"
        >
          <Edit2 className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px]" side="bottom" align="end">
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <Label
              htmlFor={`${serviceTypeKey}-target-revenue`}
              className="text-left font-medium"
            >
              {t("Aktueller Umsatz pro Stunde (berechnet)")}
            </Label>
            <Value
              id={`${serviceTypeKey}-target-revenue`}
              value={serviceGroup.targetRevenue}
              fractions={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Label
              htmlFor={`${serviceTypeKey}-set-revenue`}
              className="text-left font-medium"
            >
              {t("Neues Umsatzziel pro Stunde")}
            </Label>
            <FloatNumberInput
              id={`${serviceTypeKey}-set-revenue`}
              className="justify-self-end"
              value={serviceGroup.targetRevenue}
              onChange={(val) => {
                setRequest({
                  ...request,
                  targetRevenue: val,
                });
              }}
              handleEnterKeyDown={(val) => {
                setRequest({ ...request, targetRevenue: val });
                onSubmit();
              }}
              disabled={isLoading}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              className="h-fit w-fit p-1"
              size="icon"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
            <Button
              className="h-fit w-fit p-1"
              size="icon"
              variant={isLoading ? "outline" : "default"}
              onClick={onSubmit}
              disabled={isLoading}
            >
              {!isLoading && <Check className="h-5 w-5" />}
              {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
